import React, { useEffect, useState } from "react"
import Icon from "../Icon"

function GroupArchiveConfirmModal({
  open,
  mode = "archive",
  groupName = "",
  isSubmitting = false,
  onClose,
  onConfirm,
}) {
  const [localSubmitting, setLocalSubmitting] = useState(false)
  const isArchive = String(mode || "").toLowerCase() === "archive"
  const busy = !!isSubmitting || localSubmitting
  const title = isArchive ? "Archive this group?" : "Reactivate this group?"
  const actionLabel = isArchive ? "Archive" : "Reactivate"
  const busyLabel = isArchive ? "Archiving..." : "Reactivating..."
  const targetName = String(groupName || "this group")

  useEffect(() => {
    if (!open) {
      setLocalSubmitting(false)
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !busy) {
        onClose?.()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [busy, onClose, open])

  if (!open) return null

  const handleClose = () => {
    if (busy) return
    onClose?.()
  }

  const handleConfirm = async () => {
    if (busy) return

    setLocalSubmitting(true)
    try {
      const result = await onConfirm?.()
      if (result?.ok === false) return
      onClose?.()
    } catch (error) {
      console.error("Archive confirmation failed", error)
    } finally {
      setLocalSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-3xl border border-orange-200 bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="group-archive-confirm-title"
      >
        <div className="border-b border-orange-100 bg-gradient-to-r from-amber-50 via-orange-50 to-red-50 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <div
                className={`mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border shadow-sm ${
                  isArchive
                    ? "border-red-200 bg-gradient-to-br from-red-500 to-orange-500 text-white"
                    : "border-amber-200 bg-gradient-to-br from-amber-500 to-red-500 text-white"
                }`}
              >
                <Icon name={isArchive ? "archive" : "arrow-counterclockwise"} size="1.05rem" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-orange-700">
                  Warning
                </p>
                <h3 id="group-archive-confirm-title" className="text-base font-bold text-gray-900">
                  {title}
                </h3>
                <p className="mt-1 text-xs text-gray-600">
                  {targetName}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleClose}
              disabled={busy}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-white/80 hover:text-gray-700 disabled:opacity-60"
              aria-label="Close confirmation dialog"
            >
              <Icon name="x-lg" size="0.9rem" />
            </button>
          </div>
        </div>

        <div className="space-y-3 px-5 py-4">
          <p className="text-sm leading-6 text-gray-700">
            {isArchive
              ? `This will move "${targetName}" out of your Start tab. `
              : `This will move "${targetName}" back into your Start tab. `}
            <span className="font-semibold text-gray-900">
              The group itself keeps working normally.
            </span>
          </p>

          <div className="rounded-2xl border border-orange-200 bg-orange-50/90 px-4 py-3">
            <div className="flex items-start gap-2">
              <Icon
                name="exclamation-triangle-fill"
                size="0.95rem"
                className="mt-0.5 shrink-0 text-orange-600"
              />
              <div className="text-xs leading-5 text-orange-900/90">
                <p className="font-semibold">
                  {isArchive ? "Archive is personal only." : "Reactivate is personal only."}
                </p>
                <p>
                  {isArchive
                    ? "It only hides the group from your active list."
                    : "It restores the group to your active list and editing mode."}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-orange-100 bg-white px-5 py-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
              isArchive ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"
            }`}
          >
            {busy ? busyLabel : `Yes, ${actionLabel}`}
          </button>
        </div>
      </div>
    </div>
  )
}

export default GroupArchiveConfirmModal
